import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, CircularProgress } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import PatientDatas from "src/modules/medcard/patient-datas";
import { useAppSelector } from "src/app/hooks";
import { mcGeneralDiseaseService, McGeneralDiseaseItem } from "src/api/medcards/mcGeneralDiseaseService";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import { useTranslate } from "src/locales";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { parseISO } from "date-fns";
import { toLocalISO } from "src/utils/date/dateHelper";

interface DiseasesModalProps {
  open: boolean;
  onClose: () => void;
  patientId?: string | number;
  editData?: McGeneralDiseaseItem | null;
}

const DiseasesModal: React.FC<DiseasesModalProps> = ({
  open,
  onClose,
  patientId,
  editData,
}) => {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => (state as any).auth);

  const [operationDate, setOperationDate] = useState<Date | null>(new Date());
  const [operationUser, setOperationUser] = useState<string>("");
  const [firstDiagnosisDate, setFirstDiagnosisDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [diseaseName, setDiseaseName] = useState<string>("");
  const [diseaseId, setDiseaseId] = useState<number | null>(null);
  const [selectedDiseaseItem, setSelectedDiseaseItem] = useState<any>(null);
  const [spirometryName, setSpirometryName] = useState<string>("");
  const [spirometryId, setSpirometryId] = useState<number | null>(null);
  const [selectedSpirometryItem, setSelectedSpirometryItem] = useState<any>(null);
  const [note, setNote] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState(false);

  // Kullanıcı adını otomatik olarak login durumundan al
  useEffect(() => {
    if (user?.username) {
      setOperationUser(user.username);
    }
  }, [user]);

  // Form sıfırlama fonksiyonu
  const resetForm = () => {
    setOperationDate(new Date());
    setOperationUser(user?.username || "");
    setFirstDiagnosisDate(new Date());
    setEndDate(new Date());
    setDiseaseName("");
    setDiseaseId(null);
    setSelectedDiseaseItem(null);
    setSpirometryName("");
    setSpirometryId(null);
    setSelectedSpirometryItem(null);
    setNote("");
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setLoading(false);
  };

  // Modal açıldığında/kapandığında form'u yönet
  useEffect(() => {
    if (!open) {
      // Modal kapandığında form'u sıfırla
      resetForm();
      return;
    }

    // Modal açıldığında
    if (open && editData) {
      // Edit modunda: editData ile form'u doldur
      setDiseaseId(editData.pid || null);
      setDiseaseName(editData.name || "");
      setSelectedDiseaseItem({
        id: editData.pid,
        name: editData.name,
      });
      setSpirometryId(editData.spirometryid || null);
      setSpirometryName(editData.spirometryid ? `Spirometri ID: ${editData.spirometryid}` : "");
      setSelectedSpirometryItem(editData.spirometryid ? {
        id: editData.spirometryid,
        name: `Spirometri ID: ${editData.spirometryid}`,
        displayField: `Spirometri ID: ${editData.spirometryid}`,
      } : null);
      setNote(editData.note || editData.privatenotes || "");
      if (editData.firstdiagdate) {
        try {
          const date = typeof editData.firstdiagdate === 'string' ? parseISO(editData.firstdiagdate) : new Date(editData.firstdiagdate as any);
          setFirstDiagnosisDate(date);
        } catch (e) {
          setFirstDiagnosisDate(new Date());
        }
      }
      if (editData.enddate) {
        try {
          const date = typeof editData.enddate === 'string' ? parseISO(editData.enddate) : new Date(editData.enddate as any);
          setEndDate(date);
        } catch (e) {
          setEndDate(new Date());
        }
      }
      if (editData.cuser) {
        setOperationUser(editData.cuser);
      }
      if (editData.ctime) {
        try {
          const date = typeof editData.ctime === 'string' ? parseISO(editData.ctime) : new Date(editData.ctime);
          setOperationDate(date);
        } catch (e) {
          setOperationDate(new Date());
        }
      }
    } else if (open && !editData) {
      // Yeni kayıt modunda: form'u sıfırla
      resetForm();
    }
  }, [open, editData, user?.username]);

  const handleSave = async () => {
    // Validation
    if (!patientId) {
      setSnackbarMessage(t("disease.errors.patient-id-not-found"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    // Edit modunda güncelleme
    if (editData) {
      if (!editData.id) {
        setSnackbarMessage(t("disease.errors.id-not-found"));
        setSnackbarType("error");
        setSnackbarOpen(true);
        return;
      }

      if (!firstDiagnosisDate) {
        setSnackbarMessage(t("disease.errors.first-diagnosis-date-not-selected"));
        setSnackbarType("error");
        setSnackbarOpen(true);
        return;
      }

      // Bitiş tarihi tanı tarihinden önce olamaz
      if (endDate && firstDiagnosisDate && endDate < firstDiagnosisDate) {
        setSnackbarMessage(t("disease.errors.end-date-before-diagnosis"));
        setSnackbarType("error");
        setSnackbarOpen(true);
        return;
      }

      setLoading(true);
      try {
        await mcGeneralDiseaseService.updateDisease({
          id: editData.id,
          firstdiagdate: toLocalISO(firstDiagnosisDate || new Date()),
          finishdate: endDate ? toLocalISO(endDate) : undefined,
          spirometryid: spirometryId || 0,
          note: note || "",
        });

        setSnackbarMessage(t("disease.success.save-success"));
        setSnackbarType("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          onClose();
        }, 1500);
      } catch (error: any) {
        console.error("Hastalık güncelleme hatası:", error);
        const errorMessage = error?.response?.data?.error || error?.error || t("disease.errors.save-error");
        setSnackbarMessage(errorMessage);
        setSnackbarType("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Yeni kayıt
    if (!diseaseId) {
      setSnackbarMessage(t("disease.errors.disease-not-selected"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    if (!firstDiagnosisDate) {
      setSnackbarMessage(t("disease.errors.first-diagnosis-date-not-selected"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    // Bitiş tarihi tanı tarihinden önce olamaz
    if (endDate && firstDiagnosisDate && endDate < firstDiagnosisDate) {
      setSnackbarMessage("Bitiş tarihi tanı tarihinden önce olamaz");
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        ptid: Number(patientId),
        processid: diseaseId,
        firstdiagdate: toLocalISO(firstDiagnosisDate || new Date()),
        finishdate: endDate ? toLocalISO(endDate) : undefined,
        spirometryid: spirometryId || 0,
        note: note || "",
      };

      await mcGeneralDiseaseService.addDisease(requestData);
      setSnackbarMessage(t("disease.success.save-success"));
      setSnackbarType("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error("Hastalık kaydetme hatası:", error);
      const errorMessage = error?.response?.data?.error || error?.error || t("disease.errors.save-error");
      setSnackbarMessage(errorMessage);
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          py: { xs: 2, sm: 3 },
          px: { xs: 1.5, sm: 2 },
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {/* Başlık */}
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#f4f7fe",
            borderBottom: "1px solid #e0e0e0",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "flex-start",
            py: 1.5,
            px: 2.5,
            mb: 2,
          }}
        >
          <ByzMuiTypography isTitle={true} sx={{ fontSize: 20, lineHeight: "22px", color: "#111827" }}>
            {t("disease.title")}
          </ByzMuiTypography>
        </Box>

        {/* Save / Close */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 1,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} disabled={loading} />
          <Box sx={{ flex: 1 }} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Hasta bilgisi kartı (grafiksiz) */}
        <Box sx={{ mb: 2 }}>
          <PatientDatas patientId={patientId} isCreateMedCard noMargin />
        </Box>

        {/* Form alanları */}
        <Box
          sx={{
            mt: 1,
            width: { xs: "100%", md: "60%" },
          }}
        >
          {/* Bölüm başlığı - Hastalık */}
          <Box
            sx={{
              backgroundColor: "#F4F7FE",
              borderBottom: "1px solid #E0E0E0",
              mb: 1.5,
              px: 3,
              py: 1,
            }}
          >
            <ByzMuiTypography isTitle={true}>
              {t("disease.title")}
            </ByzMuiTypography>
          </Box>

          <Grid container spacing={0}>
            {/* İşlem Tarihi */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("disease.operation-date")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiDateTimePicker
                    value={operationDate}
                    onChange={(val) => setOperationDate(val as Date | null)}
                    width="100%"
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* İşlem Yapan Kullanıcı */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("disease.operation-user")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="İşlem Yapan Kullanıcı"
                    value={operationUser}
                    onChange={(e) => setOperationUser(e.target.value)}
                    fullWidth
                    cantChange
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* İlk Tanı Tarihi */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("disease.first-diagnosis-date")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiDateTimePicker
                    value={firstDiagnosisDate}
                    onChange={(val) => setFirstDiagnosisDate(val as Date | null)}
                    width="100%"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Bitiş Tarihi */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("disease.end-date")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiDateTimePicker
                    value={endDate}
                    onChange={(val) => setEndDate(val as Date | null)}
                    width="100%"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Hastalık (select) */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("disease.title")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzSelectListPopupInput
                    label={t("buttons.chose") || "Seçiniz"}
                    placeholder={t("buttons.chose") || "Seçiniz"}
                    value={diseaseName}
                    selectedItem={selectedDiseaseItem}
                    disabled={!!editData}
                    singleSelection={true}
                    onChange={(value: any) => {
                      // Tek seçim için direkt item'ı al
                      const item = value;
                      if (item) {
                        setSelectedDiseaseItem(item);
                        setDiseaseId(item?.id || null);
                        setDiseaseName(item?.name || "");
                      } else {
                        setSelectedDiseaseItem(null);
                        setDiseaseId(null);
                        setDiseaseName("");
                      }
                    }}
                    url="/reference/api/McGeneralDisease/field-illnesses-illnes"
                    method="GET"
                    dialogTitle={t("disease.dialog-title")}
                    displayField="name"
                    columns={[
                      { field: "id", headerName: "ID", width: 100 },
                      { field: "name", headerName: t("disease.disease"), flex: 1 },
                      { field: "category", headerName: t("disease.category"), width: 150 },
                    ]}
                    dialogShowOkButton={true}
                    rowID={patientId}
                    showPatientData={false}
                    fullWidth
                    sx={{ width: "100%" }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Spirometri (SNET Kronik Hastalık) - select */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("disease.spirometry")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzSelectListPopupInput
                    label={t("buttons.chose") || "Seçiniz"}
                    placeholder={t("buttons.chose") || "Seçiniz"}
                    value={spirometryName}
                    selectedItem={selectedSpirometryItem}
                    disabled={!!editData}
                    singleSelection={true}
                    onChange={(value: any) => {
                      // Tek seçim için direkt item'ı al
                      const item = value;
                      if (item) {
                        setSelectedSpirometryItem(item);
                        setSpirometryId(item?.ID || item?.id || null);
                        setSpirometryName(item?.NAME || item?.name || "");
                      } else {
                        setSelectedSpirometryItem(null);
                        setSpirometryId(null);
                        setSpirometryName("");
                      }
                    }}
                    url="/medcard/v1/GeneralHealthInformation/GetSpirometryList/V1"
                    method="POST"
                    postData={{
                      patientId: patientId || 0,
                    }}
                    dialogTitle={t("disease.dialog-title-spirometry")}
                    displayField="NAME"
                    columns={[
                      { field: "ID", headerName: "ID", width: 100 },
                      { field: "NAME", headerName: t("disease.spirometry"), flex: 1 },
                    ]}
                    dialogShowOkButton={true}
                    rowID={patientId}
                    showPatientData={false}
                    fullWidth
                    sx={{ width: "100%" }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Not */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("disease.note")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label={t("disease.note")}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Snackbar */}
      <ByzMuiSnackBar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        title={snackbarType === "success" ? t("common.success") : t("common.error")}
        message={snackbarMessage}
        type={snackbarType}
      />
    </OpenComponentDialog>
  );
};

export default DiseasesModal;


